import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import { ArrowLeft, Shield, FolderOpen, Check, X, Eye, EyeOff, Copy, RefreshCw, Link, QrCode, Download } from 'lucide-react';
import QRCode from 'react-qr-code';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminSettings = () => {
  const navigate = useNavigate();
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Passcode setup state
  const [showPasscodeDialog, setShowPasscodeDialog] = useState(false);
  const [passcode, setPasscode] = useState('');
  const [confirmPasscode, setConfirmPasscode] = useState(false);
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  
  // Log path state
  const [showLogPathDialog, setShowLogPathDialog] = useState(false);
  const [logPath, setLogPath] = useState('');
  
  // Email feature state
  const [emailFeatureEnabled, setEmailFeatureEnabled] = useState(false);
  
  // Registration key state
  const [registrationKey, setRegistrationKey] = useState('');
  const [regenerating, setRegenerating] = useState(false);

  // Agent registration key state
  const [agentRegistrationKey, setAgentRegistrationKey] = useState('');
  const [agentRegenerating, setAgentRegenerating] = useState(false);

  // Provider registration key state
  const [providerRegistrationKey, setProviderRegistrationKey] = useState('');
  const [providerRegenerating, setProviderRegenerating] = useState(false);
  
  // Notification pool state
  const [notificationPoolSize, setNotificationPoolSize] = useState(30);
  const [savingPoolSize, setSavingPoolSize] = useState(false);

  // Max attachment size state
  const [maxAttachmentSize, setMaxAttachmentSize] = useState(30);
  const [savingAttachmentSize, setSavingAttachmentSize] = useState(false);

  // Upload settings state
  const [imageCompression, setImageCompression] = useState(false);
  const [chunkedUpload, setChunkedUpload] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('adminToken');
    if (!token) {
      navigate('/admin/login');
      return;
    }
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('adminToken');
      const [settingsRes, siteSettingsRes, regKeyRes, agentRegKeyRes, providerRegKeyRes] = await Promise.all([
        axios.get(`${API}/admin/settings`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/site-settings`),
        axios.get(`${API}/admin/site-settings/registration-key`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/admin/site-settings/agent-registration-key`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/admin/site-settings/provider-registration-key`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      setSettings(settingsRes.data);
      setLogPath(settingsRes.data.log_path || '');
      setNotificationPoolSize(settingsRes.data.notification_pool_size ?? 30);
      setEmailFeatureEnabled(siteSettingsRes.data.email_feature_enabled ?? false);
      setMaxAttachmentSize(siteSettingsRes.data.max_attachment_size_mb ?? 30);
      setImageCompression(siteSettingsRes.data.image_compression_enabled ?? false);
      setChunkedUpload(siteSettingsRes.data.chunked_upload_enabled ?? false);
      setRegistrationKey(regKeyRes.data.registration_key || '');
      setAgentRegistrationKey(agentRegKeyRes.data.agent_registration_key || '');
      setProviderRegistrationKey(providerRegKeyRes.data.provider_registration_key || '');
    } catch (error) {
      console.error('Failed to fetch settings:', error);
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const handleRegenerateAgentKey = async () => {
    setAgentRegenerating(true);
    try {
      const token = localStorage.getItem('adminToken');
      const res = await axios.post(`${API}/admin/site-settings/regenerate-agent-registration-key`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAgentRegistrationKey(res.data.agent_registration_key);
      toast.success('Agent registration key regenerated');
    } catch (error) {
      toast.error('Failed to regenerate agent key');
    } finally {
      setAgentRegenerating(false);
    }
  };

  const handleRegenerateProviderKey = async () => {
    setProviderRegenerating(true);
    try {
      const token = localStorage.getItem('adminToken');
      const res = await axios.post(`${API}/admin/site-settings/regenerate-provider-registration-key`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProviderRegistrationKey(res.data.provider_registration_key);
      toast.success('Provider registration key regenerated');
    } catch (error) {
      toast.error('Failed to regenerate provider key');
    } finally {
      setProviderRegenerating(false);
    }
  };


  const handleSetupPasscode = async () => {
    if (passcode.length !== 6 || !/^\d+$/.test(passcode)) {
      toast.error('Passcode must be exactly 6 digits');
      return;
    }

    if (!confirmPasscode) {
      setConfirmPasscode(true);
      return;
    }

    if (!password) {
      toast.error('Please enter your current password');
      return;
    }

    try {
      const token = localStorage.getItem('adminToken');
      await axios.post(`${API}/admin/setup-passcode`, {
        passcode,
        current_password: password
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Passcode set up successfully');
      setShowPasscodeDialog(false);
      setPasscode('');
      setPassword('');
      setConfirmPasscode(false);
      fetchSettings();
    } catch (error) {
      console.error('Failed to setup passcode:', error);
      toast.error(error.response?.data?.detail || 'Failed to setup passcode');
    }
  };

  const handleUpdateLogPath = async () => {
    if (!logPath.trim()) {
      toast.error('Please enter a valid log path');
      return;
    }

    try {
      const token = localStorage.getItem('adminToken');
      await axios.put(`${API}/admin/log-path`, {
        log_path: logPath
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Log path updated successfully');
      setShowLogPathDialog(false);
      fetchSettings();
    } catch (error) {
      console.error('Failed to update log path:', error);
      toast.error(error.response?.data?.detail || 'Failed to update log path');
    }
  };

  const handleResetLogPath = async () => {
    if (!window.confirm('Reset log path to default?')) return;

    try {
      const token = localStorage.getItem('adminToken');
      await axios.delete(`${API}/admin/log-path`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Log path reset to default');
      setLogPath('');
      fetchSettings();
    } catch (error) {
      console.error('Failed to reset log path:', error);
      toast.error('Failed to reset log path');
    }
  };

  const handleToggleQuickSignIn = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.put(`${API}/admin/quick-signin-toggle`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success(response.data.message);
      fetchSettings();
    } catch (error) {
      console.error('Failed to toggle Quick Sign-In:', error);
      toast.error('Failed to update setting');
    }
  };

  const handleToggleEmailFeature = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.put(`${API}/admin/site-settings/email-feature-toggle`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const newValue = response.data.email_feature_enabled;
      setEmailFeatureEnabled(newValue);
      toast.success(`Email feature ${newValue ? 'enabled' : 'disabled'}`);
    } catch (error) {
      console.error('Failed to toggle email feature:', error);
      toast.error('Failed to update setting');
    }
  };

  const handleRegenerateKey = async () => {
    if (!window.confirm('Regenerating the key will invalidate the current registration link. Continue?')) return;
    setRegenerating(true);
    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.post(`${API}/admin/site-settings/regenerate-registration-key`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRegistrationKey(response.data.registration_key);
      toast.success('Registration key regenerated');
    } catch (error) {
      toast.error('Failed to regenerate key');
    } finally {
      setRegenerating(false);
    }
  };

  const getRegistrationUrl = () => {
    return `${window.location.origin}/onboard/${registrationKey}`;
  };

  const handleSavePoolSize = async () => {
    const size = parseInt(notificationPoolSize, 10);
    if (isNaN(size) || size < 1 || size > 1000) {
      toast.error('Pool size must be between 1 and 1000');
      return;
    }
    setSavingPoolSize(true);
    try {
      const token = localStorage.getItem('adminToken');
      await axios.put(`${API}/admin/notification-pool-size`, { size }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Notification pool size updated to ${size}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update pool size');
    } finally {
      setSavingPoolSize(false);
    }
  };

  const handleSaveAttachmentSize = async () => {
    const size = parseInt(maxAttachmentSize, 10);
    if (isNaN(size) || size < 1 || size > 100) {
      toast.error('Attachment size must be between 1 and 100 MB');
      return;
    }
    setSavingAttachmentSize(true);
    try {
      const token = localStorage.getItem('adminToken');
      await axios.put(`${API}/admin/site-settings`, { max_attachment_size_mb: size }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Max attachment size updated to ${size} MB`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update attachment size');
    } finally {
      setSavingAttachmentSize(false);
    }
  };

  const handleToggleUploadSetting = async (field, value) => {
    try {
      const token = localStorage.getItem('adminToken');
      await axios.put(`${API}/admin/site-settings`, { [field]: value }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (field === 'image_compression_enabled') {
        setImageCompression(value);
        toast.success(`Image compression ${value ? 'enabled' : 'disabled'}`);
      } else {
        setChunkedUpload(value);
        toast.success(`Chunked upload ${value ? 'enabled' : 'disabled'}`);
      }
    } catch (error) {
      toast.error('Failed to update setting');
    }
  };



  const handleCopyUrl = () => {
    navigator.clipboard.writeText(getRegistrationUrl());
    toast.success('Registration URL copied to clipboard');
  };

  const downloadQR = (containerId, filename) => {
    const svg = document.getElementById(containerId)?.querySelector('svg');
    if (!svg) return toast.error('QR code not found');
    const svgData = new XMLSerializer().serializeToString(svg);
    const canvas = document.createElement('canvas');
    canvas.width = 600;
    canvas.height = 600;
    const ctx = canvas.getContext('2d');
    const img = new Image();
    img.onload = () => {
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, 600, 600);
      ctx.drawImage(img, 0, 0, 600, 600);
      const link = document.createElement('a');
      link.download = `${filename}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
      toast.success('QR Code downloaded!');
    };
    img.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgData)));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-stone-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#1A4D2E]"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-50">
      {/* Header */}
      <div className="bg-white border-b border-stone-200 py-3 md:py-4 px-4 md:px-6 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <div className="flex items-center gap-4">
            <Button
              onClick={() => navigate('/admin/dashboard')}
              variant="ghost"
              size="sm"
              className="text-stone-600 hover:text-[#1A4D2E]"
            >
              <ArrowLeft size={18} className="mr-2" />
              Back
            </Button>
            <h1 className="text-xl md:text-3xl font-bold text-[#1A4D2E]" style={{fontFamily: '"Playfair Display", serif'}}>
              Admin Settings
            </h1>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto p-4 md:p-6 space-y-6">
        {/* Account Info */}
        <Card className="p-6">
          <h2 className="text-lg font-semibold text-stone-900 mb-4">Account Information</h2>
          <div className="space-y-3">
            <div>
              <label className="text-sm text-stone-600">Email</label>
              <p className="text-stone-900 font-medium">{settings?.email}</p>
            </div>
            <div>
              <label className="text-sm text-stone-600">Name</label>
              <p className="text-stone-900 font-medium">{settings?.name || 'N/A'}</p>
            </div>
          </div>
        </Card>

        {/* Security Settings */}
        <Card className="p-6">
          <div className="flex items-center gap-3 mb-4">
            <Shield className="text-[#1A4D2E]" size={24} />
            <h2 className="text-lg font-semibold text-stone-900">Security</h2>
          </div>
          
          <div className="space-y-4">
            <div className="flex items-center justify-between p-4 bg-stone-50 rounded-lg">
              <div>
                <p className="font-medium text-stone-900">Passcode Protection</p>
                <p className="text-sm text-stone-600">
                  {settings?.has_passcode ? 
                    'Passcode is set up for sensitive operations' : 
                    'Set up a 6-digit passcode for extra security'}
                </p>
              </div>
              <Button
                onClick={() => setShowPasscodeDialog(true)}
                variant="outline"
                className={settings?.has_passcode ? 'border-green-500 text-green-600' : 'border-[#1A4D2E] text-[#1A4D2E]'}
              >
                {settings?.has_passcode ? (
                  <><Check size={16} className="mr-2" /> Update Passcode</>
                ) : (
                  <><Shield size={16} className="mr-2" /> Setup Passcode</>
                )}
              </Button>
            </div>
          </div>
        </Card>

        {/* System Configuration */}
        <Card className="p-6">
          <div className="flex items-center gap-3 mb-4">
            <FolderOpen className="text-[#1A4D2E]" size={24} />
            <h2 className="text-lg font-semibold text-stone-900">System Configuration</h2>
          </div>
          
          <div className="space-y-4">
            {/* Quick Sign-In Toggle */}
            <div className="p-4 bg-stone-50 rounded-lg">
              <div className="flex items-center justify-between mb-2">
                <div>
                  <p className="font-medium text-stone-900">Quick Sign-In Feature</p>
                  <p className="text-sm text-stone-600 mt-1">
                    Enable a simplified sign-in form for agents to quickly capture leads
                  </p>
                </div>
                <Button
                  onClick={handleToggleQuickSignIn}
                  variant="outline"
                  className={settings?.show_quick_signin ? 'bg-green-50 border-green-500 text-green-700' : 'border-stone-300 text-stone-600'}
                >
                  {settings?.show_quick_signin ? (
                    <><Check size={16} className="mr-2" /> Enabled</>
                  ) : (
                    <><X size={16} className="mr-2" /> Disabled</>
                  )}
                </Button>
              </div>
            </div>

            {/* Email Feature Toggle */}
            <div className="p-4 bg-stone-50 rounded-lg">
              <div className="flex items-center justify-between mb-2">
                <div>
                  <p className="font-medium text-stone-900">Send Email Feature</p>
                  <p className="text-sm text-stone-600 mt-1">
                    Show or hide the "Send Thank You Emails" button on event detail pages
                  </p>
                </div>
                <Button
                  onClick={handleToggleEmailFeature}
                  data-testid="email-feature-toggle"
                  variant="outline"
                  className={emailFeatureEnabled ? 'bg-green-50 border-green-500 text-green-700' : 'border-stone-300 text-stone-600'}
                >
                  {emailFeatureEnabled ? (
                    <><Check size={16} className="mr-2" /> Enabled</>
                  ) : (
                    <><X size={16} className="mr-2" /> Disabled</>
                  )}
                </Button>
              </div>
            </div>

            {/* Max Attachment Size */}
            <div className="p-4 bg-stone-50 rounded-lg">
              <div className="mb-2">
                <p className="font-medium text-stone-900">Max Email Attachment Size</p>
                <p className="text-sm text-stone-600 mt-1">
                  Maximum file size allowed for email attachments (1–100 MB)
                </p>
              </div>
              <div className="flex items-center gap-2 mt-3">
                <Input
                  type="number"
                  min={1}
                  max={100}
                  value={maxAttachmentSize}
                  onChange={(e) => setMaxAttachmentSize(e.target.value)}
                  className="w-28"
                  data-testid="max-attachment-size-input"
                />
                <span className="text-sm text-stone-500">MB</span>
                <Button
                  onClick={handleSaveAttachmentSize}
                  disabled={savingAttachmentSize}
                  size="sm"
                  className="bg-[#1A4D2E] text-white hover:bg-[#143D24]"
                  data-testid="save-attachment-size-btn"
                >
                  {savingAttachmentSize ? 'Saving...' : 'Save'}
                </Button>
              </div>

              <div className="mt-4 pt-4 border-t border-stone-200 space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-stone-900">Image Compression</p>
                    <p className="text-xs text-stone-500">Compress images (JPG, PNG, WEBP) before upload to reduce file size</p>
                  </div>
                  <Switch
                    checked={imageCompression}
                    onCheckedChange={(val) => handleToggleUploadSetting('image_compression_enabled', val)}
                    data-testid="image-compression-toggle"
                  />
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-stone-900">Chunked Upload</p>
                    <p className="text-xs text-stone-500">Split large files (PDF, DOCX) into chunks to bypass upload size limits</p>
                  </div>
                  <Switch
                    checked={chunkedUpload}
                    onCheckedChange={(val) => handleToggleUploadSetting('chunked_upload_enabled', val)}
                    data-testid="chunked-upload-toggle"
                  />
                </div>
              </div>
            </div>

            {/* Notification Pool Size */}
            <div className="p-4 bg-stone-50 rounded-lg">
              <div className="mb-2">
                <p className="font-medium text-stone-900">Notification Pool Size</p>
                <p className="text-sm text-stone-600 mt-1">
                  Polling interval in seconds for fetching new notifications (1–1000)
                </p>
              </div>
              <div className="flex items-center gap-2 mt-3">
                <Input
                  type="number"
                  min={1}
                  max={1000}
                  value={notificationPoolSize}
                  onChange={(e) => setNotificationPoolSize(e.target.value)}
                  className="w-28"
                  data-testid="notification-pool-size-input"
                />
                <Button
                  onClick={handleSavePoolSize}
                  disabled={savingPoolSize}
                  size="sm"
                  className="bg-[#1A4D2E] text-white hover:bg-[#143D24]"
                  data-testid="save-notification-pool-size-btn"
                >
                  {savingPoolSize ? 'Saving...' : 'Save'}
                </Button>
              </div>
            </div>

            {/* Registration URLs */}
            <div className="p-4 bg-stone-50 rounded-lg">
              <div className="flex items-center gap-2 mb-4">
                <QrCode size={20} className="text-[#1A4D2E]" />
                <p className="text-lg font-semibold text-stone-900">Registration URLs</p>
              </div>
              <p className="text-sm text-stone-600 mb-5">
                Share these links or QR codes to allow new users to self-register. Regenerating a key invalidates the old link.
              </p>

              <div className="space-y-6">
                {/* Broker */}
                {registrationKey && (
                  <div className="border border-stone-200 rounded-lg p-4 bg-white" data-testid="broker-reg-section">
                    <p className="font-medium text-stone-900 mb-1">Broker Registration</p>
                    <p className="text-xs text-stone-500 mb-3">For new brokers to register and get admin approval</p>
                    <div className="flex flex-col sm:flex-row gap-4">
                      <div id="qr-broker-reg" className="flex-shrink-0 bg-white p-2 rounded-lg border border-stone-100 self-start">
                        <QRCode value={getRegistrationUrl()} size={120} />
                      </div>
                      <div className="flex-1 min-w-0 space-y-2">
                        <div className="bg-stone-50 border border-stone-200 rounded-lg px-3 py-2 text-xs text-stone-700 font-mono truncate" data-testid="registration-url-display">
                          {getRegistrationUrl()}
                        </div>
                        <div className="flex flex-wrap gap-2">
                          <Button onClick={handleCopyUrl} variant="outline" size="sm" className="text-[#1A4D2E]" data-testid="copy-registration-url-btn">
                            <Copy size={14} className="mr-1" /> Copy URL
                          </Button>
                          <Button onClick={() => downloadQR('qr-broker-reg', 'broker-registration-qr')} variant="outline" size="sm" className="text-[#1A4D2E]" data-testid="download-broker-qr-btn">
                            <Download size={14} className="mr-1" /> Download QR
                          </Button>
                          <Button onClick={handleRegenerateKey} variant="outline" size="sm" className="text-orange-600" disabled={regenerating} data-testid="regenerate-key-btn">
                            <RefreshCw size={14} className={`mr-1 ${regenerating ? 'animate-spin' : ''}`} />
                            {regenerating ? '...' : 'Regenerate'}
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Agent */}
                {agentRegistrationKey && (
                  <div className="border border-stone-200 rounded-lg p-4 bg-white" data-testid="agent-reg-section">
                    <p className="font-medium text-stone-900 mb-1">Agent Registration</p>
                    <p className="text-xs text-stone-500 mb-3">For agents to self-register without broker association</p>
                    <div className="flex flex-col sm:flex-row gap-4">
                      <div id="qr-agent-reg" className="flex-shrink-0 bg-white p-2 rounded-lg border border-stone-100 self-start">
                        <QRCode value={`${window.location.origin}/agent-onboard/${agentRegistrationKey}`} size={120} />
                      </div>
                      <div className="flex-1 min-w-0 space-y-2">
                        <div className="bg-stone-50 border border-stone-200 rounded-lg px-3 py-2 text-xs text-stone-700 font-mono truncate" data-testid="agent-registration-url-display">
                          {`${window.location.origin}/agent-onboard/${agentRegistrationKey}`}
                        </div>
                        <div className="flex flex-wrap gap-2">
                          <Button onClick={() => { navigator.clipboard.writeText(`${window.location.origin}/agent-onboard/${agentRegistrationKey}`); toast.success('Agent registration URL copied'); }} variant="outline" size="sm" className="text-[#1A4D2E]" data-testid="copy-agent-registration-url-btn">
                            <Copy size={14} className="mr-1" /> Copy URL
                          </Button>
                          <Button onClick={() => downloadQR('qr-agent-reg', 'agent-registration-qr')} variant="outline" size="sm" className="text-[#1A4D2E]" data-testid="download-agent-qr-btn">
                            <Download size={14} className="mr-1" /> Download QR
                          </Button>
                          <Button onClick={handleRegenerateAgentKey} variant="outline" size="sm" className="text-orange-600" disabled={agentRegenerating} data-testid="regenerate-agent-key-btn">
                            <RefreshCw size={14} className={`mr-1 ${agentRegenerating ? 'animate-spin' : ''}`} />
                            {agentRegenerating ? '...' : 'Regenerate'}
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Provider */}
                {providerRegistrationKey && (
                  <div className="border border-stone-200 rounded-lg p-4 bg-white" data-testid="provider-reg-section">
                    <p className="font-medium text-stone-900 mb-1">Provider Registration</p>
                    <p className="text-xs text-stone-500 mb-3">For service providers to self-register without agent association</p>
                    <div className="flex flex-col sm:flex-row gap-4">
                      <div id="qr-provider-reg" className="flex-shrink-0 bg-white p-2 rounded-lg border border-stone-100 self-start">
                        <QRCode value={`${window.location.origin}/provider-onboard/${providerRegistrationKey}`} size={120} />
                      </div>
                      <div className="flex-1 min-w-0 space-y-2">
                        <div className="bg-stone-50 border border-stone-200 rounded-lg px-3 py-2 text-xs text-stone-700 font-mono truncate" data-testid="provider-registration-url-display">
                          {`${window.location.origin}/provider-onboard/${providerRegistrationKey}`}
                        </div>
                        <div className="flex flex-wrap gap-2">
                          <Button onClick={() => { navigator.clipboard.writeText(`${window.location.origin}/provider-onboard/${providerRegistrationKey}`); toast.success('Provider registration URL copied'); }} variant="outline" size="sm" className="text-[#1A4D2E]" data-testid="copy-provider-registration-url-btn">
                            <Copy size={14} className="mr-1" /> Copy URL
                          </Button>
                          <Button onClick={() => downloadQR('qr-provider-reg', 'provider-registration-qr')} variant="outline" size="sm" className="text-[#1A4D2E]" data-testid="download-provider-qr-btn">
                            <Download size={14} className="mr-1" /> Download QR
                          </Button>
                          <Button onClick={handleRegenerateProviderKey} variant="outline" size="sm" className="text-orange-600" disabled={providerRegenerating} data-testid="regenerate-provider-key-btn">
                            <RefreshCw size={14} className={`mr-1 ${providerRegenerating ? 'animate-spin' : ''}`} />
                            {providerRegenerating ? '...' : 'Regenerate'}
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Log Path */}
            <div className="p-4 bg-stone-50 rounded-lg">
              <div className="flex items-center justify-between mb-2">
                <p className="font-medium text-stone-900">Log Files Directory</p>
                <div className="flex gap-2">
                  <Button
                    onClick={() => setShowLogPathDialog(true)}
                    variant="outline"
                    size="sm"
                    className="text-[#1A4D2E]"
                  >
                    Configure
                  </Button>
                  {settings?.log_path && (
                    <Button
                      onClick={handleResetLogPath}
                      variant="outline"
                      size="sm"
                      className="text-red-600"
                    >
                      Reset
                    </Button>
                  )}
                </div>
              </div>
              <p className="text-sm text-stone-600">
                Current: <code className="bg-white px-2 py-1 rounded text-xs">
                  {settings?.log_path || 'Default (auto-detect)'}
                </code>
              </p>
            </div>
          </div>
        </Card>

        {/* Data Migration */}
        <Card className="p-6" data-testid="migration-card">
          <h3 className="text-xl font-bold text-[#1A4D2E] mb-1">Data Migration</h3>
          <p className="text-sm text-stone-500 mb-4">One-time migration tools for system upgrades.</p>

          <div className="border border-stone-200 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium text-stone-800">Backfill Broker Stamps</p>
                <p className="text-sm text-stone-500 mt-1">
                  Stamps <code className="bg-stone-100 px-1 rounded text-xs">broker_id_at_creation</code> on existing events, visits, and leads. Required before using broker transfers. Safe to run multiple times.
                </p>
              </div>
              <Button
                variant="outline"
                className="border-blue-200 text-blue-700 hover:bg-blue-50 ml-4 shrink-0"
                onClick={async () => {
                  if (!window.confirm('Run broker stamp migration? This is safe and idempotent.')) return;
                  const token = localStorage.getItem('adminToken');
                  try {
                    const res = await axios.post(`${API}/admin/migrate/backfill-broker-stamps`, {}, {
                      headers: { Authorization: `Bearer ${token}` }
                    });
                    const d = res.data;
                    toast.success(`Migration complete: ${d.events_updated} events, ${d.visits_updated} visits, ${d.masters_updated} leads updated`);
                  } catch (err) {
                    toast.error('Migration failed');
                  }
                }}
                data-testid="run-migration-btn"
              >
                <RefreshCw size={16} className="mr-2" />
                Run Migration
              </Button>
            </div>
          </div>
        </Card>
      </div>

      {/* Passcode Setup Dialog */}
      <Dialog open={showPasscodeDialog} onOpenChange={setShowPasscodeDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {settings?.has_passcode ? 'Update Passcode' : 'Setup Passcode'}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            <div>
              <label className="text-sm font-medium text-stone-700">
                {confirmPasscode ? 'Re-enter 6-Digit Passcode' : 'Enter 6-Digit Passcode'}
              </label>
              <Input
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={6}
                value={passcode}
                onChange={(e) => setPasscode(e.target.value.replace(/\D/g, ''))}
                placeholder="••••••"
                className="text-2xl tracking-widest text-center font-mono"
              />
              <p className="text-xs text-stone-500 mt-1">
                This passcode will be required for sensitive operations like deleting logs or editing master data
              </p>
            </div>

            {confirmPasscode && (
              <div>
                <label className="text-sm font-medium text-stone-700">Current Password</label>
                <div className="relative">
                  <Input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Enter your current password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-600"
                  >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>
            )}
          </div>

          <DialogFooter className="mt-6">
            <Button variant="outline" onClick={() => {
              setShowPasscodeDialog(false);
              setPasscode('');
              setPassword('');
              setConfirmPasscode(false);
            }}>
              Cancel
            </Button>
            <Button onClick={handleSetupPasscode} className="bg-[#1A4D2E]">
              {confirmPasscode ? 'Confirm' : 'Continue'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Log Path Configuration Dialog */}
      <Dialog open={showLogPathDialog} onOpenChange={setShowLogPathDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Configure Log Directory</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            <div>
              <label className="text-sm font-medium text-stone-700">Log Directory Path</label>
              <Input
                type="text"
                value={logPath}
                onChange={(e) => setLogPath(e.target.value)}
                placeholder="/var/log/supervisor"
                className="font-mono text-sm"
              />
              <p className="text-xs text-stone-500 mt-1">
                Enter the full path to the directory containing log files. The path must exist and be readable.
              </p>
            </div>

            <div className="p-3 bg-blue-50 border border-blue-200 rounded text-sm text-blue-800">
              <p className="font-semibold mb-1">Common log paths:</p>
              <ul className="text-xs space-y-1 font-mono">
                <li>• /var/log/supervisor</li>
                <li>• /app/logs</li>
                <li>• /logs</li>
                <li>• /var/log</li>
              </ul>
            </div>
          </div>

          <DialogFooter className="mt-6">
            <Button variant="outline" onClick={() => setShowLogPathDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleUpdateLogPath} className="bg-[#1A4D2E]">
              Save Path
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminSettings;
